// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import React, { useMemo } from 'react'
import { ColumnFilterButton, ColumnFilterChips, funnelToServerOps, type ColumnFilters, type FunnelServerOp, type ServerFilterOptions } from './ColumnFilter'
import { useColumnWidths, ColumnResizeHandle } from './ColumnResize'
import { useColumnManager } from '../ColumnManager'
import SkeletonRows from '../SkeletonRows'

// =====================================================================================
// D-PROVGRID-1 WS2 — the list table, once.
//
// WHY THIS EXISTS
// The pharmacy Prescriptions table has header funnels, drag-resizable columns, a Columns
// manager and click-to-sort. Every one of those is a reusable unit that already shipped
// (ColumnFilter, ColumnResize, ColumnManager) — but the only place they had ever been ASSEMBLED
// was inside StationQueue, 1,400 lines deep and welded to the pharmacy list's data shape. So the
// four provider-portal tables were hand-rolled <table> markup with none of it, and the answer to
// "give the provider the same table" was, structurally, "paste StationQueue's header again".
//
// This is that assembly, extracted and made data-shape agnostic. It owns the CHROME — colgroup
// and widths, the header (label, sort affordance, funnel, resize grip), the skeleton and the
// empty state — and nothing else. It does not fetch, does not page and does not decide what a
// filter MEANS: the page passes rows in and gets user intent out. That boundary is what lets the
// provider surfaces adopt it without inheriting the pharmacy list's projection, its workbench
// columns or its tab machinery.
//
// E-RULE NOTE. The shared kit is the right long-term home for this. It is deliberately built from
// the units the pharmacy list already uses rather than a new grid library, because the two tables
// must stay recognisably the SAME table — a second implementation is how they drift, and drift
// here is what the provider portal has been living with.
// =====================================================================================

export interface ListTableColumn<T> {
  key: string
  label: string
  render: (row: T) => React.ReactNode
  align?: 'left' | 'right' | 'center'
}

/**
 * What the SERVER says this column can do. Supplied by the page from the registry's capability
 * report — never hand-maintained here. A column with no entry is treated as display-only: it
 * renders, it does not sort, and its funnel says why rather than pretending to work.
 */
export interface ListTableCap {
  sortable?: boolean
  filterable?: boolean
  facetable?: boolean
  klass?: 'configured' | 'enum' | 'entity' | 'date' | 'numeric' | 'refused'
  /** The operators the server admits on this column. `contains` is what gives a never-listed
   *  column (a name, an MRN) its funnel: a text box instead of a value list. */
  ops?: string[]
  reason?: string | null
}

/** A column the server filters by CONTAINS and never enumerates. */
export const isTextCap = (cap: ListTableCap | undefined): boolean =>
  !!cap?.filterable && !cap.facetable && cap.klass !== 'date' && cap.klass !== 'numeric'
  && (cap.ops ? cap.ops.includes('contains') : cap.klass === 'refused')

export interface ListTableProps<T> {
  /** Stable per surface — drives width + column-manager persistence (e.g. `prov:rx`). */
  viewKey: string
  columns: ListTableColumn<T>[]
  rows: T[]
  loading?: boolean
  getRowKey: (row: T, i: number) => string

  /** Server capabilities, keyed by column key. Absent entry = display-only. */
  caps?: Record<string, ListTableCap>

  /** Active column filters, and the page's setter. The page decides what to do with them
   *  (here: send them to the server); this component only collects intent. */
  filters?: ColumnFilters
  onFilters?: (next: ColumnFilters) => void
  /** Server-backed option source for a facetable column. */
  fetchOptions?: (colKey: string, term: string) => Promise<ServerFilterOptions>
  /** Entity filters store IDs — without this the checkbox list and chips show raw GUIDs. */
  labelFor?: (colKey: string, value: string) => string | undefined
  onLabels?: (colKey: string, pairs: { value: string; label: string }[]) => void

  sortKey?: string | null
  sortDir?: 'asc' | 'desc'
  onSort?: (key: string) => void

  onRowOpen?: (row: T) => void
  rowTitle?: string
  emptyText?: string
  skeletonRows?: number
  /** Optional trailing column (quick actions). Header cell + per-row cell. */
  trailingHeader?: React.ReactNode
  renderTrailing?: (row: T) => React.ReactNode
}

const th: React.CSSProperties = {
  position: 'relative', textAlign: 'left', padding: '9px 10px', fontSize: 11, fontWeight: 700,
  letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-mute)',
  borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap',
}
const td: React.CSSProperties = {
  padding: '10px', fontSize: 13.5, borderBottom: '1px solid var(--color-border)', verticalAlign: 'top',
}

export function ListTable<T>({
  viewKey, columns, rows, loading, getRowKey,
  caps, filters, onFilters, fetchOptions, labelFor, onLabels,
  sortKey, sortDir, onSort, onRowOpen, rowTitle,
  emptyText = 'Nothing to show.', skeletonRows = 8,
  trailingHeader, renderTrailing,
}: ListTableProps<T>) {
  const labels = useMemo(
    () => Object.fromEntries(columns.map(c => [c.key, c.label])), [columns])

  // The user's own column choice, on top of whatever the pharmacy configured. Persisted per view.
  const { visibleColumns, managerUI, toggleButton } = useColumnManager(
    viewKey, columns.map(c => c.key), labels)

  const visible = useMemo(() => {
    const byKey = new Map(columns.map(c => [c.key, c]))
    return visibleColumns.map(k => byKey.get(k)).filter((c): c is ListTableColumn<T> => !!c)
  }, [columns, visibleColumns])

  const colResize = useColumnWidths(viewKey, visible.map(c => c.key))

  // What the user's own column manager is suppressing, separate from what the pharmacy configured.
  const hiddenCount = columns.length - visible.length
  const resetColumns = () => {
    try { localStorage.removeItem(`owl_cols_v2_${viewKey}`) } catch { /* blocked storage: nothing to clear */ }
    // The hook reads its pref on viewId change only, so a reload is the honest way to re-read it
    // rather than reaching into its internals from out here.
    window.location.reload()
  }

  const activeFilters = filters ?? {}
  const setFilter = (key: string, next: Set<string>) =>
    onFilters?.({ ...activeFilters, [key]: next })

  const sortIcon = (key: string) =>
    sortKey === key ? <span aria-hidden="true" style={{ fontSize: 10 }}>{sortDir === 'asc' ? ' ▲' : ' ▼'}</span> : null

  const colSpan = visible.length + (trailingHeader ? 1 : 0)

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
        {/* A per-user hide is invisible once the manager is closed, so a column the pharmacy
            configured can look like a column the product failed to render. State it, and give the
            way back in the same breath. */}
        {hiddenCount > 0 && (
          <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
            {hiddenCount} column{hiddenCount === 1 ? '' : 's'} hidden by you
            <button type="button" onClick={resetColumns}
              style={{ marginLeft: 6, border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--color-accent, #0f766e)', textDecoration: 'underline' }}>
              show all
            </button>
          </span>
        )}
        {toggleButton}
      </div>
      {managerUI}
      {onFilters && (
        <ColumnFilterChips
          filters={activeFilters}
          labels={labels}
          onClear={k => onFilters({ ...activeFilters, [k]: new Set() })}
          onClearAll={() => onFilters({})}
          labelFor={labelFor}
        />
      )}
      <div style={{ overflowX: 'auto' }}>
        <table
          ref={colResize.tableRef}
          className={colResize.tableClass}
          style={{
            width: colResize.totalWidth ?? '100%',
            borderCollapse: 'collapse',
            tableLayout: colResize.tableLayout,
          }}>
          {/* Widths bind through <colgroup> under table-layout:fixed — the same mechanism the
              pharmacy list uses, so a drag here behaves exactly as it does there. */}
          <colgroup>
            {visible.map(c => <col key={c.key} style={{ width: colResize.widthFor(c.key) }} />)}
            {trailingHeader ? <col style={{ width: 40 }} /> : null}
          </colgroup>
          <thead>
            <tr>
              {visible.map((col, ci) => {
                const cap = caps?.[col.key]
                // Explicit true, never "not explicitly false". With the capability report absent
                // (offline, 404, a role that cannot read it) every header would otherwise present
                // itself as sortable and then quietly sort by nothing — a control that lies about
                // what it does is worse than a control that is plainly unavailable.
                const canSort = cap?.sortable === true && !!onSort
                return (
                  <th
                    key={col.key}
                    style={{ ...th, textAlign: col.align ?? 'left', cursor: canSort ? 'pointer' : 'default', userSelect: 'none' }}
                    onClick={() => { if (canSort) onSort!(col.key) }}
                    onKeyDown={e => {
                      if (!canSort) return
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort!(col.key) }
                    }}
                    tabIndex={canSort ? 0 : -1}
                    role="columnheader"
                    aria-sort={sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    {col.label}{sortIcon(col.key)}{' '}
                    {onFilters && (() => {
                      // Every column the server can filter has a funnel (D-UI-2). What differs
                      // is the control inside it: a value list for a facetable column, from/to
                      // bounds for a date or a number, and a CONTAINS box for a column that is
                      // never enumerated (a name, an MRN, free-text directions).
                      //
                      // The marker is left for exactly one case: the server REFUSES the column to
                      // this user, which today means the pharmacy masks it for their role. A
                      // filter on a masked value would disclose what the mask hides, so that is a
                      // refusal with its reason shown, not a missing feature.
                      const rangeMode = cap?.klass === 'date' || cap?.klass === 'numeric'
                      const textMode = isTextCap(cap)
                      if (!cap?.facetable && !rangeMode && !textMode) return (
                        <span
                          title={cap?.reason ?? `${col.label} cannot be filtered on this list.`}
                          aria-label={`${col.label} filter unavailable`}
                          style={{ display: 'inline-block', width: 14, opacity: 0.25, cursor: 'not-allowed', fontSize: 11 }}>⌀</span>
                      )
                      return (
                        <ColumnFilterButton
                          label={col.label}
                          fetchOptions={cap?.facetable && fetchOptions ? (term: string) => fetchOptions(col.key, term) : undefined}
                          values={[]}
                          labelFor={labelFor ? (v: string) => labelFor(col.key, v) : undefined}
                          onLabels={onLabels ? pairs => onLabels(col.key, pairs) : undefined}
                          isDate={cap?.klass === 'date'}
                          isNumeric={cap?.klass === 'numeric'}
                          isText={textMode}
                          selected={activeFilters[col.key] ?? new Set()}
                          onChange={next => setFilter(col.key, next)} />
                      )
                    })()}
                    <ColumnResizeHandle colKey={col.key} colIndex={ci} api={colResize} label={col.label} />
                  </th>
                )
              })}
              {trailingHeader ? <th style={{ ...th, width: 40 }}>{trailingHeader}</th> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={skeletonRows} cols={colSpan} />
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} style={{ ...td, textAlign: 'center', color: 'var(--color-text-mute)', padding: '28px 10px' }}>
                  {emptyText}
                </td>
              </tr>
            ) : rows.map((row, i) => (
              <tr
                key={getRowKey(row, i)}
                onDoubleClick={() => onRowOpen?.(row)}
                onKeyDown={e => { if (e.key === 'Enter') onRowOpen?.(row) }}
                tabIndex={onRowOpen ? 0 : -1}
                role={onRowOpen ? 'button' : undefined}
                title={onRowOpen ? (rowTitle ?? 'Double-click to open') : undefined}
                style={{ cursor: onRowOpen ? 'pointer' : 'default' }}>
                {visible.map(col => (
                  <td key={col.key} style={{ ...td, textAlign: col.align ?? 'left' }}>{col.render(row)}</td>
                ))}
                {renderTrailing ? <td style={td}>{renderTrailing(row)}</td> : trailingHeader ? <td style={td} /> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default ListTable

/**
 * Translate the header funnels' state into SERVER filters, using the registry's capability report.
 *
 * A column the server cannot filter is reported as BLOCKED rather than quietly dropped or replayed
 * over the rows on screen. Filtering the loaded page and presenting the result as the whole set is
 * the defect this whole layer exists to remove; silently ignoring the funnel is the same lie with
 * less effort.
 *
 * Date and numeric funnels carry their bounds inside the Set as `≥value` / `≤value`, which is the
 * encoding ColumnFilterButton produces for a range — one bound encoding, shared with the pharmacy
 * list, so both surfaces translate to identical predicates.
 */
export function toServerFilters(
  colFilters: ColumnFilters,
  caps: Record<string, ListTableCap & { ops?: string[] }>,
): { serverFilters: { key: string; op: FunnelServerOp; values: string[] }[]; blockedCols: string[] } {
  const serverFilters: { key: string; op: FunnelServerOp; values: string[] }[] = []
  const blockedCols: string[] = []
  for (const [key, set] of Object.entries(colFilters)) {
    if (!set || set.size === 0) continue
    const cap = caps[key]
    if (!cap?.filterable) { blockedCols.push(key); continue }
    const { preds, blocked } = funnelToServerOps(set, cap.ops)
    for (const p of preds) serverFilters.push({ key, ...p })
    if (blocked) blockedCols.push(key)
  }
  return { serverFilters, blockedCols }
}
